import React, { useMemo } from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { MdDragIndicator } from "react-icons/md";
import { IoClose, IoChevronBack, IoChevronForward } from "react-icons/io5";
import IconButton from "../kit/IconButton";
import "./SortableImageStrip.css";

const restrictToHorizontalAxis = ({ transform }) => ({
  ...transform,
  y: 0,
});

const getDefaultItemId = (item, imageSrc, index) => {
  return imageSrc || item?.original || item?.base64 || item?.file?.name || `image-${index}`;
};

const SortableImageThumb = ({
  canReorder,
  id,
  imageSrc,
  index,
  isSelected,
  itemCount,
  onMove,
  onRemove,
  onSelect,
}) => {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id, disabled: !canReorder });

  return (
    <ImageThumb
      ref={setNodeRef}
      attributes={canReorder ? attributes : undefined}
      canReorder={canReorder}
      imageSrc={imageSrc}
      index={index}
      isDragging={isDragging}
      isSelected={isSelected}
      itemCount={itemCount}
      listeners={canReorder ? listeners : undefined}
      onMove={onMove}
      onRemove={onRemove}
      onSelect={onSelect}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: isDragging ? "box-shadow 0.18s ease, opacity 0.18s ease" : transition,
      }}
    />
  );
};

const ImageThumb = React.forwardRef(({
  attributes,
  canReorder,
  imageSrc,
  index,
  isDragging = false,
  isOverlay = false,
  isSelected,
  itemCount,
  listeners,
  onMove,
  onRemove,
  onSelect,
  style,
}, ref) => {
  return (
    <div
      ref={ref}
      className={`sortable-image-thumb ${
        isSelected ? "sortable-image-thumb--active" : ""
      } ${isDragging ? "sortable-image-thumb--dragging" : ""} ${
        isOverlay ? "sortable-image-thumb--overlay" : ""
      }`}
      onClick={() => onSelect?.(index)}
      style={style}
      {...attributes}
      {...listeners}
    >
      <div className="sortable-image-index">{index + 1}</div>
      <img
        src={imageSrc}
        alt={`Thumbnail ${index + 1}`}
        className="sortable-image-img"
        draggable={false}
      />
      {onRemove && !isOverlay && (
        <IconButton
          label="remove image"
          variant="overlay"
          size="xs"
          className="absolute right-kit-4 top-kit-4 z-[2]"
          icon={<IoClose />}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(index);
          }}
          onPointerDown={(event) => event.stopPropagation()}
        />
      )}
      {canReorder && (
        <div className="sortable-image-controls">
          <IconButton
            label="move image earlier"
            variant="overlay"
            size="xs"
            icon={<IoChevronBack />}
            disabled={index === 0}
            onClick={(event) => {
              event.stopPropagation();
              onMove(index, index - 1);
            }}
            onPointerDown={(event) => event.stopPropagation()}
          />
          <MdDragIndicator className="sortable-image-grip" size={18} />
          <IconButton
            label="move image later"
            variant="overlay"
            size="xs"
            icon={<IoChevronForward />}
            disabled={index === itemCount - 1}
            onClick={(event) => {
              event.stopPropagation();
              onMove(index, index + 1);
            }}
            onPointerDown={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
});

const SortableImageStrip = ({
  items,
  getImageSrc,
  selectedIndex = 0,
  onSelect,
  onReorder,
  onRemove,
  helpText = "drag photos or use arrows to reorder",
  className = "",
}) => {
  const itemList = items || [];
  const canReorder = Boolean(onReorder) && itemList.length > 1;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 6 },
    })
  );
  const sortableItems = useMemo(() => {
    const seenIds = new Map();

    return itemList.map((item, index) => {
      const imageSrc = getImageSrc(item);
      const baseId = String(getDefaultItemId(item, imageSrc, index));
      const count = seenIds.get(baseId) || 0;
      seenIds.set(baseId, count + 1);

      return {
        id: count > 0 ? `${baseId}-${count}` : baseId,
        imageSrc,
        item,
      };
    });
  }, [getImageSrc, itemList]);

  if (itemList.length === 0) return null;

  const commitReorder = (fromIndex, toIndex) => {
    if (
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0 ||
      fromIndex >= itemList.length ||
      toIndex >= itemList.length
    ) {
      return;
    }

    const nextItems = arrayMove(itemList, fromIndex, toIndex);

    onReorder?.(nextItems, toIndex);
    onSelect?.(toIndex);
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = sortableItems.findIndex((item) => item.id === active.id);
    const newIndex = sortableItems.findIndex((item) => item.id === over.id);
    commitReorder(oldIndex, newIndex);
  };

  return (
    <div className={`sortable-image-strip ${className}`.trim()}>
      {canReorder && <div className="sortable-image-help">{helpText}</div>}
      <div className="sortable-image-scroll">
        <DndContext
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={handleDragEnd}
          sensors={sensors}
        >
          <SortableContext
            items={sortableItems.map((item) => item.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div className="sortable-image-list">
              {sortableItems.map((item, index) => (
                <SortableImageThumb
                  key={item.id}
                  canReorder={canReorder}
                  id={item.id}
                  imageSrc={item.imageSrc}
                  index={index}
                  isSelected={selectedIndex === index}
                  itemCount={itemList.length}
                  onMove={commitReorder}
                  onRemove={onRemove}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default SortableImageStrip;
